import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ColorPicker, useColor } from 'react-color-palette';
import 'react-color-palette/css';
import { toast } from 'react-toastify';
import { getBaseApiUrl } from 'src/config/apiConfig';
import {
  DynamicBrandConfig,
  useDynamicBrand,
} from 'src/context/DynamicBrandContext';
import { useApplicationGroups } from 'src/hooks/useApplicationGroups';
import { useUpdateApplicationGroupMutation } from 'src/services/switch-rest-api/application-group.service';
import InfoTooltip from 'switch-fe-shared/components/Tooltips/InfoTooltip';

interface ColorPickerFieldProps {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}

function ColorPickerField({ label, value, onChange }: ColorPickerFieldProps) {
  const [color, setColor] = useColor(value);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        onClick={() => setOpen(true)}
        title='Click to edit color'
        sx={{ cursor: 'pointer' }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: value,
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        />
        <Stack>
          <Typography variant='caption' color='text.secondary'>
            {label}
          </Typography>
          <Typography variant='body2'>{value}</Typography>
        </Stack>
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent sx={{ p: 0 }}>
          <ColorPicker
            color={color}
            onChange={(c) => {
              setColor(c);
              onChange(c.hex);
            }}
            height={150}
            hideAlpha
            hideInput={['rgb', 'hsv']}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

interface DraftConfig {
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  logoLongUrl: string;
  email: string;
  website: string;
  number: string;
}

function configToDraft(
  config: DynamicBrandConfig,
  fallbackPrimary: string,
  fallbackSecondary: string
): DraftConfig {
  return {
    brandName: config.brandName ?? '',
    primaryColor: config.primaryColor ?? fallbackPrimary,
    secondaryColor: config.secondaryColor ?? fallbackSecondary,
    logoUrl: config.logoUrl ?? '',
    logoLongUrl: config.logoLongUrl ?? '',
    email: config.contactDetails?.email ?? '',
    website: config.contactDetails?.website ?? '',
    number: config.contactDetails?.number ?? '',
  };
}

const BrandSettingsDrawer = () => {
  const {
    config,
    setConfig,
    clearConfig,
    resetToDefaults,
    drawerOpen,
    setDrawerOpen,
    selectedAppGroupId,
  } = useDynamicBrand();
  const theme = useTheme();
  const [updateApplicationGroup] = useUpdateApplicationGroupMutation();
  const { refetch: refetchApplicationGroups } = useApplicationGroups();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoLongInputRef = useRef<HTMLInputElement>(null);

  const [pendingSmallLogo, setPendingSmallLogo] = useState<File | null>(null);
  const [pendingWideLogo, setPendingWideLogo] = useState<File | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const [draft, setDraft] = useState<DraftConfig>(() =>
    configToDraft(
      config,
      theme.palette.primary.main,
      theme.palette.secondary.main
    )
  );

  /** Sync draft when the drawer opens or the context config changes externally. */
  useEffect(() => {
    if (drawerOpen) {
      setDraft(
        configToDraft(
          config,
          theme.palette.primary.main,
          theme.palette.secondary.main
        )
      );
    }
  }, [drawerOpen]);

  const updateDraft = useCallback(
    (partial: Partial<DraftConfig>) =>
      setDraft((prev) => ({ ...prev, ...partial })),
    []
  );

  const handleSmallLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingSmallLogo(file);
  };

  const handleWideLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingWideLogo(file);
  };

  /** Push draft to the dynamic brand context. */
  const handleApply = useCallback(() => {
    const update: Partial<DynamicBrandConfig> = {
      brandName: draft.brandName,
      primaryColor: draft.primaryColor,
      secondaryColor: draft.secondaryColor,
      contactDetails: {
        email: draft.email,
        website: draft.website,
        number: draft.number,
      },
    };
    if (pendingSmallLogo) {
      update.logoUrl = URL.createObjectURL(pendingSmallLogo);
      update.faviconUrl = URL.createObjectURL(pendingSmallLogo);
    } else if (draft.logoUrl) {
      update.logoUrl = draft.logoUrl;
    }
    if (pendingWideLogo) {
      update.logoLongUrl = URL.createObjectURL(pendingWideLogo);
    } else if (draft.logoLongUrl) {
      update.logoLongUrl = draft.logoLongUrl;
    }
    setConfig(update);
  }, [draft, pendingSmallLogo, pendingWideLogo, setConfig]);

  const handleReset = useCallback(async () => {
    resetToDefaults();
    setPendingSmallLogo(null);
    setPendingWideLogo(null);
    setDraft(
      configToDraft(
        { brandName: 'Switch Energy' } as DynamicBrandConfig,
        theme.palette.primary.main,
        theme.palette.secondary.main
      )
    );

    if (!selectedAppGroupId) return;

    setIsSaving(true);
    const t = toast.loading('Resetting brand settings...');

    try {
      await updateApplicationGroup({
        id: selectedAppGroupId,
        data: {
          brand: {
            colors: [],
            contactDetails: {
              supportEmail: '',
              supportNumber: '',
              website: '',
            },
            assets: {
              favicon: { bucket: '', key: '' },
              logo: { bucket: '', key: '' },
              headerImage: { bucket: '', key: '' },
            },
          } as any,
        },
      }).unwrap();

      await refetchApplicationGroups();

      toast.dismiss(t);
      toast.success('Brand settings reset');
    } catch (err) {
      toast.dismiss(t);
      toast.error(`Failed to reset brand settings: ${err}`);
    } finally {
      setIsSaving(false);
    }
  }, [
    resetToDefaults,
    theme,
    selectedAppGroupId,
    updateApplicationGroup,
    refetchApplicationGroups,
  ]);

  const handleSave = useCallback(async () => {
    if (!selectedAppGroupId) {
      toast.error('No application group selected');
      return;
    }

    // Apply first so context is up to date
    handleApply();

    setIsSaving(true);
    const t = toast.loading('Saving brand settings...');

    try {
      // Upload pending image files
      const uploadPromises: Promise<void>[] = [];

      if (pendingSmallLogo) {
        const formData = new FormData();
        formData.append('file', pendingSmallLogo);
        uploadPromises.push(
          axios
            .post(
              `${getBaseApiUrl()}application-group/${selectedAppGroupId}/brand/favicon/upload`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            )
            .then(() => {
              setPendingSmallLogo(null);
            })
        );
      }

      if (pendingWideLogo) {
        const formData = new FormData();
        formData.append('file', pendingWideLogo);
        uploadPromises.push(
          axios
            .post(
              `${getBaseApiUrl()}application-group/${selectedAppGroupId}/brand/logo/upload`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            )
            .then(() => {
              setPendingWideLogo(null);
            })
        );
      }

      await Promise.all(uploadPromises);

      // PATCH text fields (name, colors, contact details)
      const colors = [draft.primaryColor, draft.secondaryColor].filter(
        Boolean
      ) as string[];

      await updateApplicationGroup({
        id: selectedAppGroupId,
        data: {
          ...(draft.brandName ? { name: draft.brandName } : {}),
          brand: {
            ...(colors.length > 0 ? { colors } : {}),
            contactDetails: {
              supportEmail: draft.email,
              website: draft.website,
              supportNumber: draft.number,
            },
          },
        },
      }).unwrap();

      // Refresh application groups so context picks up saved data
      await refetchApplicationGroups();

      // Clear local overrides — the saved data now comes from the AppGroup
      clearConfig();

      toast.dismiss(t);
      toast.success('Brand settings saved');
    } catch (err) {
      toast.dismiss(t);
      toast.error(`Failed to save brand settings: ${err}`);
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedAppGroupId,
    draft,
    pendingSmallLogo,
    pendingWideLogo,
    updateApplicationGroup,
    refetchApplicationGroups,
    clearConfig,
    handleApply,
  ]);

  return (
    <>
      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 360, p: 2 } }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Stack direction='row' alignItems='center'>
            <Typography variant='h6'>Brand Settings</Typography>
            <InfoTooltip title='Customise brand details for demos. Changes are temporary (cleared when the browser closes) unless explicitly saved to an Application Group. Use "Reset to Default" to revert all changes.' />
          </Stack>
          <IconButton onClick={() => setDrawerOpen(false)} size='small'>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2.5}>
          {/* Brand Name */}
          <TextField
            label='Brand Name'
            size='small'
            value={draft.brandName}
            onChange={(e) => updateDraft({ brandName: e.target.value })}
            fullWidth
          />

          {/* Colors */}
          <Typography variant='subtitle2'>Colors</Typography>

          <ColorPickerField
            label='Primary Color'
            value={draft.primaryColor}
            onChange={(hex) => updateDraft({ primaryColor: hex })}
          />

          <ColorPickerField
            label='Secondary Color'
            value={draft.secondaryColor}
            onChange={(hex) => updateDraft({ secondaryColor: hex })}
          />

          <Divider />

          {/* Logos */}
          <Typography variant='subtitle2'>Logos</Typography>

          <Box>
            <Button
              variant='outlined'
              size='small'
              onClick={() => logoInputRef.current?.click()}
              fullWidth
            >
              Upload Icon Logo
            </Button>
            <input
              ref={logoInputRef}
              type='file'
              accept='image/*'
              hidden
              onChange={handleSmallLogoChange}
            />
            {(pendingSmallLogo || draft.logoUrl) && (
              <Box mt={1} textAlign='center'>
                <img
                  src={
                    pendingSmallLogo
                      ? URL.createObjectURL(pendingSmallLogo)
                      : draft.logoUrl
                  }
                  alt='Icon logo preview'
                  style={{
                    maxHeight: 48,
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
          </Box>

          <Box>
            <Button
              variant='outlined'
              size='small'
              onClick={() => logoLongInputRef.current?.click()}
              fullWidth
            >
              Upload Full Logo
            </Button>
            <input
              ref={logoLongInputRef}
              type='file'
              accept='image/*'
              hidden
              onChange={handleWideLogoChange}
            />
            {(pendingWideLogo || draft.logoLongUrl) && (
              <Box mt={1} textAlign='center'>
                <img
                  src={
                    pendingWideLogo
                      ? URL.createObjectURL(pendingWideLogo)
                      : draft.logoLongUrl
                  }
                  alt='Full logo preview'
                  style={{
                    maxHeight: 48,
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
          </Box>

          <Divider />

          {/* Contact Details */}
          <Typography variant='subtitle2'>Contact Details</Typography>

          <TextField
            label='Email'
            size='small'
            value={draft.email}
            onChange={(e) => updateDraft({ email: e.target.value })}
            fullWidth
          />

          <TextField
            label='Website'
            size='small'
            value={draft.website}
            onChange={(e) => updateDraft({ website: e.target.value })}
            fullWidth
          />

          <TextField
            label='Phone Number'
            size='small'
            value={draft.number}
            onChange={(e) => updateDraft({ number: e.target.value })}
            fullWidth
          />

          <Divider />

          {/* Apply */}
          <Button
            variant='contained'
            startIcon={<CheckIcon />}
            onClick={handleApply}
            title='Preview changes temporarily — resets on page refresh'
            fullWidth
          >
            Apply
          </Button>

          {/* Save */}
          <Button
            variant='contained'
            color='secondary'
            startIcon={
              isSaving ? <CircularProgress size={18} /> : <SaveOutlinedIcon />
            }
            onClick={handleSave}
            disabled={isSaving || !selectedAppGroupId}
            fullWidth
          >
            {isSaving ? 'Saving...' : 'Save to Application Group'}
          </Button>

          {/* Reset */}
          <Button
            variant='outlined'
            color='error'
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            disabled={isSaving}
            fullWidth
          >
            Reset to Default
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};

export default BrandSettingsDrawer;
